import { apiGet, apiPost } from './apiUtils';

const feedbackService = {
  /**
   * Submit feedback for a lead
   * @param {string} leadId - Lead ID
   * @param {string} feedback - Feedback text
   * @returns {Promise<Object>} Submission response
   */
  submitFeedback: async (payload) => {
    const { lead_id, feedback } = payload;

    if (!lead_id || !feedback) {
      console.error('❌ Invalid input: lead_id and feedback are required', payload);
      return {
        success: false,
        message: 'Lead ID and feedback text are required',
        error: 'Invalid input'
      };
    }

    try {
      console.log('📝 Preparing feedback submission:', payload);

      // Submit feedback as a lead event
      const eventPayload = {
        lead_id: lead_id,
        event_type: 'feedback_added', // Matches mobile app's event type
        description: feedback
      };

      console.log('📤 Submitting payload:', JSON.stringify(eventPayload, null, 2));

      const response = await apiPost('/api/crm/lead-events', eventPayload);

      console.log('✅ Feedback Submission Response:', JSON.stringify(response, null, 2));

      return {
        success: true,
        message: 'Feedback submitted successfully',
        data: response
      };
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      return {
        success: false,
        message: error.message || 'Failed to submit feedback',
        error: error
      };
    }
  },

  /**
   * Fetch feedbacks for a lead
   * @param {string} leadId - Lead ID
   * @returns {Promise<Object>} Feedback data
   */
  getFeedbacksByLead: async (leadId) => {
    if (!leadId) {
      console.error('❌ Invalid input: leadId is required');
      return {
        success: false,
        message: 'Lead ID is required',
        error: 'Invalid input',
        data: []
      };
    }

    try {
      console.log('🔍 Fetching feedbacks for lead:', leadId);

      // Fetch feedback events (only feedback_added events)
      const response = await apiGet(`/api/crm/lead-events?lead_id=${leadId}&event_type=feedback_added`);

      console.log('✅ Feedback Fetch Response:', JSON.stringify(response, null, 2));

      if (response.success) {
        // Transform data to match frontend requirements
        const transformedFeedbacks = (response.data || []).map(item => ({
          id: item.id,
          date: item.created_at,
          text: item.description,
          author: item.created_by_name || 'Unknown'
        }));

        return {
          success: true,
          data: transformedFeedbacks
        };
      } else {
        console.warn('⚠️ No feedbacks found');
        return {
          success: false,
          message: 'No feedbacks found',
          data: []
        };
      }
    } catch (error) {
      console.error('❌ Error fetching feedbacks:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch feedbacks',
        error: error,
        data: []
      };
    }
  },

  /**
   * Add lead event (matching mobile app API call)
   * @param {Object} eventData - Event data
   * @returns {Promise<Object>} Response
   */
  addLeadEvent: async (eventData) => {
    try {
      console.log('📝 Adding lead event:', eventData);

      const response = await apiPost('/api/crm/lead-events', eventData);

      console.log('✅ Lead event added:', response);

      return {
        success: true,
        data: response,
        message: 'Lead event added successfully'
      };
    } catch (error) {
      console.error('❌ Error adding lead event:', error);
      return {
        success: false,
        message: error.message || 'Failed to add lead event',
        error: error
      };
    }
  },

  /**
   * Format event description for display (matching mobile app)
   * @param {Object} event - Event object
   * @returns {string} Formatted description
   */
  formatEventDescription: (event) => {
    // Check if event has detailed changes array
    if (event.event_data?.changes && Array.isArray(event.event_data.changes) && event.event_data.changes.length > 0) {
      return event.event_data.changes.map(change =>
        `${change.field}: "${change.old_value}" → "${change.new_value}"`
      ).join('\n');
    }

    switch (event.event_type) {
      case 'lead_created':
        return `🆕 Lead created`;
      case 'lead_updated':
        // Try to parse description for detailed changes
        if (event.description && event.description.includes('→')) {
          return `✏️ ${event.description.replace('Lead updated: ', '')}`;
        }
        return `✏️ Lead updated: ${event.event_data?.fields?.join(', ') || event.description || 'details updated'}`;
      case 'status_changed':
        return `🔄 Lead Status: "${event.event_data?.old_status || 'N/A'}" → "${event.event_data?.new_status || 'N/A'}"`;
      case 'note_added':
        return `📝 Note: ${event.description}`;
      case 'property_searched':
        return `🔍 Property search performed`;
      case 'task_created':
        return `📌 Task created: ${event.event_data?.task_title || event.description}`;
      case 'task_completed':
        return `✔️ Task completed: ${event.event_data?.task_title || event.description}`;
      case 'feedback_added':
        // Check if this is a lead update (not actual feedback) - matching mobile app logic
        if (event.event_data?.type === 'lead_details_updated' ||
            (event.description && (event.description.startsWith('📝') || event.description.includes('Lead details updated')))) {
          // For lead updates, show the description as-is (already has emoji)
          return event.description;
        }
        return `💬 Feedback: ${event.description}`;
      case 'site_visit_scheduled':
        return `📅 Site visit scheduled for ${event.event_data?.project_name || 'project'}`;
      case 'site_visit_completed':
        return `✅ Site visit completed for ${event.event_data?.project_name || 'project'}`;
      case 'invoice_status_changed':
        return `💳 Invoice Status: "${event.event_data?.old_status || 'N/A'}" → "${event.event_data?.new_status || 'N/A'}"`;
      case 'invoice_stage_changed':
        return `📄 Invoice Stage: "${event.event_data?.old_stage || 'N/A'}" → "${event.event_data?.new_stage || 'N/A'}"`;
      case 'invoice_paid':
        return `💰 Payment initiated - Expected: ${event.event_data?.payment_time || 'N/A'}`;
      case 'payment_received':
        return `✅ Payment received and confirmed`;
      case 'payment_not_received':
        return `⚠️ Payment not received - Builder notified`;
      case 'lead_owner_changed':
        return `👤 Lead Owner: "${event.event_data?.old_owner || 'N/A'}" → "${event.event_data?.new_owner || 'N/A'}"`;
      case 'interested_projects_updated':
        return `🏠 Interested projects updated`;
      case 'marked_as_changed':
        return `🏷️ Marked as: "${event.event_data?.old_value || 'N/A'}" → "${event.event_data?.new_value || 'N/A'}"`;
      case 'builder_accepted':
        return `✅ Builder accepted the lead`;
      default:
        return event.description || `${event.event_type.replace(/_/g, ' ')} event`;
    }
  }
};

export default feedbackService;
